import { gql, useQuery } from "@apollo/client";
import type { ICaaCustomerOrdersQuery, ICaaOrder } from "@repo/shopify/queries/customer-account-gql";
import { caaCustomerOrdersQuery } from "@repo/shopify/queries/customer-account-gql";
import type { ICustomerOrdersQuery, IOrdersEdgesNode } from "@repo/shopify/queries/customer-gql";
import { customerOrdersQuery } from "@repo/shopify/queries/customer-gql";
import { useCallback, useEffect, useMemo, useRef } from "react";

import { NOTIFICATIONS } from "@const/notifications";

import apolloCaaClient from "@lib/apolloCaa";

import { useCustomerStore } from "@store/customer";
import { useLayoutStore } from "@store/layout";

export default function useOrders(isJp: boolean) {
  const { token } = useCustomerStore();
  const { showFooter, openToast } = useLayoutStore();

  const loaderEl = useRef(null);

  const onError = useCallback(
    (error: Error) => {
      openToast(NOTIFICATIONS.ERROR_SERVER);
      throw new Error(error.message);
    },
    [openToast],
  );

  const classicVariables = useMemo(
    () => ({
      customerAccessToken: token.accessToken,
      first: 10,
      sortKey: "PROCESSED_AT",
      reverse: true,
    }),
    [token.accessToken],
  );

  const classicQuery = useQuery<ICustomerOrdersQuery>(gql(customerOrdersQuery.query), {
    variables: classicVariables,
    notifyOnNetworkStatusChange: true,
    skip: isJp,
    onError,
  });

  const caaVariables = useMemo(() => ({ first: 10, sortKey: "PROCESSED_AT", reverse: true }), []);

  const caaQuery = useQuery<ICaaCustomerOrdersQuery>(gql(caaCustomerOrdersQuery.query), {
    client: apolloCaaClient,
    variables: caaVariables,
    notifyOnNetworkStatusChange: true,
    skip: !isJp,
    onError,
  });

  const { fetchMore: classicFetchMore } = classicQuery;
  const { fetchMore: caaFetchMore } = caaQuery;
  const activeData = isJp ? caaQuery.data : classicQuery.data;
  const activeLoading = isJp ? caaQuery.loading : classicQuery.loading;

  const hasNextPage = useMemo(
    () => activeData?.customer?.orders?.pageInfo.hasNextPage || false,
    [activeData?.customer?.orders?.pageInfo],
  );

  const endCursor = useMemo(
    () => activeData?.customer?.orders?.pageInfo.endCursor || "",
    [activeData?.customer?.orders?.pageInfo],
  );

  useEffect(() => {
    showFooter(!hasNextPage);
  }, [hasNextPage, showFooter]);

  const handleObserver = useCallback(
    async ([entry]: IntersectionObserverEntry[]) => {
      if (!entry.isIntersecting || !hasNextPage || activeLoading) return;

      if (isJp) {
        await caaFetchMore({
          variables: { ...caaVariables, after: endCursor },
          updateQuery: (prev, { fetchMoreResult }) => {
            if (!fetchMoreResult?.customer) return prev;

            return {
              customer: {
                ...prev.customer,
                orders: {
                  ...prev.customer?.orders,
                  edges: [...(prev.customer?.orders?.edges || []), ...(fetchMoreResult.customer.orders?.edges || [])],
                  pageInfo: fetchMoreResult.customer.orders.pageInfo,
                },
              },
            };
          },
        });
      } else {
        await classicFetchMore({
          variables: { ...classicVariables, after: endCursor },
          updateQuery: (prev, { fetchMoreResult }) => {
            if (!fetchMoreResult) return prev;

            return {
              customer: {
                ...prev.customer,
                orders: {
                  ...prev.customer?.orders,
                  edges: [...(prev.customer?.orders?.edges || []), ...(fetchMoreResult.customer?.orders?.edges || [])],
                  pageInfo: fetchMoreResult.customer.orders.pageInfo,
                },
              },
            };
          },
        });
      }
    },
    [isJp, endCursor, hasNextPage, activeLoading, caaFetchMore, caaVariables, classicFetchMore, classicVariables],
  );

  useEffect(() => {
    const observer = new IntersectionObserver(handleObserver, {
      root: null,
      rootMargin: "1000px 0px 0px 0px",
      threshold: 0.1,
    });

    if (loaderEl?.current) observer.observe(loaderEl.current);

    return () => {
      observer.disconnect();
    };
  }, [handleObserver, loaderEl]);

  return {
    initialLoading: !activeData?.customer && activeLoading,
    hasNextPage,
    loaderEl,
    orders: (activeData?.customer?.orders?.edges || []) as { node: IOrdersEdgesNode | ICaaOrder }[],
  };
}
